import type { Address } from '../../../types/shared';
import { formatAddress } from '../../../types/shared';

export function resolveAddressString(address: string | Address): string {
  if (typeof address === 'string') return address;
  return formatAddress(address);
}

export function resolveCoords(
  address: string | Address,
  lat?: number,
  lng?: number,
): { lat: number; lng: number } | null {
  if (lat != null && lng != null) return { lat, lng };
  if (typeof address !== 'string' && address.lat != null && address.lng != null) {
    return { lat: address.lat, lng: address.lng };
  }
  return null;
}

export function normalizeAddressForDocId(address: string): string {
  return address
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/[,.\s]+$/, '')
    .replace(/\//g, '-');
}

export function computeInitialPosition(
  triggerRect: DOMRect,
  panelWidth: number,
  panelHeight: number,
  gap: number = 8,
): { x: number; y: number } {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  let x: number;
  let y: number;

  if (triggerRect.right + gap + panelWidth <= vw) {
    x = triggerRect.right + gap;
  } else if (triggerRect.left - gap - panelWidth >= 0) {
    x = triggerRect.left - gap - panelWidth;
  } else {
    x = Math.max(0, (vw - panelWidth) / 2);
  }

  y = Math.max(8, Math.min(triggerRect.top, vh - panelHeight - 8));
  return { x, y };
}

export function clampToViewport(
  x: number,
  y: number,
  width: number,
  height: number,
  minVisible: number = 100,
): { x: number; y: number } {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  return {
    x: Math.max(minVisible - width, Math.min(x, vw - minVisible)),
    y: Math.max(minVisible - height, Math.min(y, vh - minVisible)),
  };
}

export function getDirectionsUrl(address: string): string {
  const encoded = encodeURIComponent(address);
  if (/Android|iPhone|iPad/i.test(navigator.userAgent)) {
    return `https://maps.google.com/?daddr=${encoded}`;
  }
  return `https://www.google.com/maps/dir/?api=1&destination=${encoded}`;
}

export function getStaticMapUrl(
  address: string,
  apiKey: string,
  width: number = 250,
  height: number = 150,
  zoom: number = 14,
  mapType: StaticMapType = 'roadmap',
): string {
  const encoded = encodeURIComponent(address);
  return `https://maps.googleapis.com/maps/api/staticmap?center=${encoded}&zoom=${zoom}&size=${width}x${height}&scale=2&maptype=${mapType}&markers=${encoded}&key=${apiKey}`;
}

export type StaticMapType = 'roadmap' | 'satellite';

export const PREVIEW_ZOOM = { min: 3, max: 20, maxSatellite: 18, initial: 14 } as const;

/** Satellite imagery stops short of the street map's maximum zoom in many rural places, and the picture then comes back as a blank "no imagery" tile. */
export const maxPreviewZoom = (mapType: StaticMapType): number => (mapType === 'satellite' ? PREVIEW_ZOOM.maxSatellite : PREVIEW_ZOOM.max);

/** The hover preview's box: the map picture and the Expand / Directions row under it. */
export const PREVIEW_BOX = { width: 260, mapHeight: 130, actionsHeight: 36 } as const;

/**
 * Where a hover preview goes so ALL of it is on screen (ISS-637). It opens below the element; when it would run off the bottom it opens
 * above instead; when it fits neither way (a short window) it is pushed to the nearest edge. Horizontally it is centred on the element and
 * kept inside the window. `margin` is the breathing room to the window edge and `gap` the space between the element and the preview.
 */
export function placePreview(
  trigger: { left: number; right: number; top: number; bottom: number; width: number },
  size: { width: number; height: number },
  viewport: { width: number; height: number },
  gap = 8,
  margin = 8,
): { left: number; top: number; placement: 'below' | 'above' } {
  const left = Math.max(margin, Math.min(trigger.left + trigger.width / 2 - size.width / 2, viewport.width - size.width - margin));
  const fitsBelow = trigger.bottom + gap + size.height <= viewport.height - margin;
  const fitsAbove = trigger.top - gap - size.height >= margin;
  if (fitsBelow || !fitsAbove) {
    const below = trigger.bottom + gap;
    const top = fitsBelow ? below : Math.max(margin, Math.min(below, viewport.height - size.height - margin));
    return { left, top, placement: 'below' };
  }
  return { left, top: trigger.top - gap - size.height, placement: 'above' };
}
